import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ODataParams, odataParamsFactory } from '@tdw/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { EnvironmentConfig, ENV_CONFIG } from '../../environments/environment.model';
import { ClosureDetail, ClosureProduct, ClosureProductAssociatedProduct, ClosureQueryResult, closureProductFactory } from '../models';
import { LookupValue } from '../models/lookup.model';
import { ODataResponse } from '../models/odata-response.model';
import { DateService } from './date.service';

@Injectable({ providedIn: 'root' })
export class ClosureService {
  constructor(
    private http: HttpClient,
    private dateService: DateService,
    @Inject(ENV_CONFIG) private config: EnvironmentConfig) { }

  private getDiameters(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureDiameters${q.toQuery()}`)
      .pipe(map(res => res.value || []));
  }
  private getDesignCodes(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const now = this.dateService.getNow();
    const q = odataParamsFactory({ orderBy: 'SortOrder', filter: `InactiveDate eq null OR InactiveDate gt ${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}` });
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureDesignCodes${q.toQuery()}`)
      .pipe(map(res => res.value || []));;
  }
  private getDesignFactors(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureDesignFactors${q.toQuery()}`)
      .pipe(map(res => res.value || []));
  }

  private getWallThicknesses(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' });
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureWallThicknesses${q.toQuery()}`)
      .pipe(map(res => res.value || []))
  }

  private getHingeOrientations(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' });
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureHingeOrientations${q.toQuery()}`)
      .pipe(map(res => res.value || []))
  }

  private getPressureRatings(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' });
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosurePressureRatings${q.toQuery()}`)
      .pipe(map(res => res.value || []));
  }

  private getClampRings(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' });
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureClampRings${q.toQuery()}`)
      .pipe(map(res => res.value || []));
  }

  private getFlangeClasses(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' });
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureFlangeClasses${q.toQuery()}`)
      .pipe(map(res => res.value || []));
  }

  private getClassRatings(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' });
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureClassRatings${q.toQuery()}`)
      .pipe(map(res => res.value || []));
  }



  getLookups(): Observable<{
    diameters: LookupValue[],
    designCodes: LookupValue[],
    designFactors: LookupValue[],
    wallThicknesses: LookupValue[],
    hingeOrientations: LookupValue[],
    pressureRatings: LookupValue[]
    clampRings: LookupValue[],
    flangeClasses: LookupValue[],
    classRatings: LookupValue[]
  }> {
    const diameters = this.getDiameters();
    const designCodes = this.getDesignCodes();
    const designFactors = this.getDesignFactors();
    const wallThicknesses = this.getWallThicknesses();
    const hingeOrientations = this.getHingeOrientations();
    const pressureRatings = this.getPressureRatings();
    const clampRings = this.getClampRings();
    const flangeClasses = this.getFlangeClasses();
    const classRatings = this.getClassRatings();

    return forkJoin([diameters,
      designCodes, designFactors, wallThicknesses, hingeOrientations, pressureRatings, clampRings, flangeClasses, classRatings]).pipe(
        map(([diameters, designCodes, designFactors, wallThicknesses, hingeOrientations, pressureRatings, clampRings, flangeClasses, classRatings]) =>
          ({ diameters, designCodes, designFactors, wallThicknesses, hingeOrientations, pressureRatings, clampRings, flangeClasses, classRatings })));
  }

  getClosureQueryResults(q: string): Observable<ClosureQueryResult[]> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ClosureQueryResult[]>(`${endpoint?.url}api/${endpoint?.version}/ClosureProducts${q}`);
  }

  getClosureDrawing(id: number): Observable<Blob> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ArrayBuffer>(`${endpoint?.url}api/${endpoint?.version}/Drawings/closure/${id}`,
      { responseType: 'arraybuffer' as 'json' })
      .pipe(map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/pdf' })));
  }

  getClosureDetail(id: number): Observable<ClosureDetail> {
    if (id === -1) return of();
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ClosureDetail>(`${endpoint?.url}api/${endpoint?.version}/ClosureProducts/${id}`);
    // .pipe(map(p => ({ ...p, image: !!p ? new Blob([p.image || '']) : null })))
  }

  getClosureMaintenance(q: ODataParams): Observable<ODataResponse<ClosureProduct[]>> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ODataResponse<ClosureProduct[]>>(`${endpoint?.url}odata/${endpoint?.version}/ClosureProducts/${q.toQuery()}`);
  }

  getClosureById(id: number): Observable<ClosureProduct | null> {
    const now = this.dateService.getNow();
    if (id === -1) return of(closureProductFactory({ id: undefined }));
    const endpoint = this.config.endpoints.baseApi;
    const q = odataParamsFactory({
      filter: `id eq ${id}`,
      expand: `ClosureProductDesignPressures($expand=ClosureDesignFactor),ClosureProductDesignCodes($filter=InactiveDate eq null OR InactiveDate gt ${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${(now.getDate()).toString().padStart(2, '0')}$expand=DesignCode),Diameter, WallThickness, ClampRing, HingeOrientation, pressureRating, FlangeClass`,
      select: ['id', 'flangeClassID', 'partNumber', 'productName', 'description', 'diameterId', 'wallThicknessID', 'designFactorID', 'hingeOrientationID', 'clampRingID', 'pressureRatingId', 'flanged', 'maxDesignPressure', 'minDesignTemp', 'maxDesignTemp', 'notes', 'restricted', 'restrictedNotice', 'inactiveDate', 'createdDate', 'createdBy', 'updatedDate', 'updatedBy', 'weight']
    });

    return this.http.get<ODataResponse<ClosureProduct[]>>(`${endpoint.url}odata/${endpoint.version}/ClosureProducts${q.toQuery()}`)
      .pipe(map(v => (v?.value || []).length > 0 ? (v.value || [])[0] : null));
  }

  getClosureByPartNumber(partNumber: string): Observable<ClosureProduct | null> {
    const now = this.dateService.getNow();
    if (!partNumber) return of(closureProductFactory({ id: undefined }));
    const endpoint = this.config.endpoints.baseApi;
    const q = odataParamsFactory({
      filter: `partNumber eq '${partNumber}'`,
      select: ['id']
    });

    return this.http.get<ODataResponse<ClosureProduct[]>>(`${endpoint.url}odata/${endpoint.version}/ClosureProducts${q.toQuery()}`)
      .pipe(map(v => (v?.value || []).length > 0 ? (v.value || [])[0] : null));
  }

  private createClosure(closure: ClosureProduct): Observable<any> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.post<any>(`${endpoint?.url}odata/${endpoint?.version}/ClosureProducts`, closure);
  }

  saveClosure(closure: ClosureProduct): Observable<any> {
    if (!closure.id) return this.createClosure(closure);

    const endpoint = this.config.endpoints?.baseApi;
    return this.http.patch<any>(`${endpoint?.url}odata/${endpoint?.version}/ClosureProducts(${closure.id})`, closure);
  }

  addDesignCode(obj: { closureProductId: number, designCodeId: number }): Observable<ClosureDetail> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.post<any>(`${endpoint?.url}odata/${endpoint?.version}/ClosureProductDesignCodes`, obj);
  }

  removeDesignCode(closureProductDesignCodeId: number): Observable<any> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.delete<any>(`${endpoint?.url}odata/${endpoint?.version}/ClosureProductDesignCodes(${closureProductDesignCodeId})`);
  }

  addDesignPressure(obj: { designFactorId: number; closureProductId: number; maxDesignPressure: number; }): Observable<any> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.post<any>(`${endpoint?.url}odata/${endpoint?.version}/ClosureProductDesignPressures`, obj);
  }

  removeDesignPressure(closureProductDesignPressureId: number) {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.delete<any>(`${endpoint?.url}odata/${endpoint?.version}/ClosureProductDesignPressures(${closureProductDesignPressureId})`);
  }

  getFlatterFilesDrawing(filename: string): Observable<Blob> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ArrayBuffer>(`${endpoint?.url}api/${endpoint?.version}/Drawings/flatterfiles/${filename}`,
      { responseType: 'arraybuffer' as 'json' })
      .pipe(map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/pdf' })));
  }
}
